/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2019. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from 'environments/environment';
import { HttpHelpersService } from '@shared/services';
import { HttpClient } from '@angular/common/http';
import { Constants } from '@shared/models/constants.model';
import { Store } from '@ngrx/store';

import * as fromSharedStore from 'app/shared/store';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProFeedbackService {
  constructor(private httpClient: HttpClient, private sharedStore: Store<fromSharedStore.SharedState>) {}

  getProFeedbackForPurchases(purchaseIds: number[]): Observable<any> {
    if (!purchaseIds.length) {
      return of([]);
    }

    return HttpHelpersService.handleGetResponse(
      this.httpClient
        .get(`${environment.apiHostname}/v1/pro-feedback/list/of-purchase-ids?purchaseIds=${purchaseIds.join(',')}`)
        .pipe()
    );
  }

  getProFeedback(): Observable<any> {
    return this.sharedStore.select(fromSharedStore.getAccessState).pipe(
      filter((state) => !state.loading),
      take(1),
      map((state) => state.products.filter((product) => product.type === Constants.PURCHASE_TYPE.PRO_FEEDBACK)),
      switchMap((pfProducts) => {
        const purchaseIds = pfProducts.map((product) => product.id);
        return this.getProFeedbackForPurchases(purchaseIds).pipe(
          map((proFeedbackItems) => {
            return pfProducts.map((product) => {
              const proFeedbackItem = proFeedbackItems.find((item) => item.purchase.id === product.id);
              if (proFeedbackItem) {
                return {
                  ...product,
                  item: proFeedbackItem,
                };
              }

              return product;
            });
          })
        );
      })
    );
  }

  /**
   * Submit pro feedback form
   *
   * @returns {Observable<any>}
   */
  submitProFeedbackForm(data: any) {
    for (const playInfo of data.playsInfo) {
      playInfo.event = playInfo.eventId > 0 ? { id: playInfo.eventId } : null;
      playInfo.game = playInfo.gameId > 0 ? { id: playInfo.gameId } : null;
      playInfo.clip = playInfo.clipId > 0 ? { id: playInfo.clipId } : null;
    }
    const saveData = {
      ...data.athleteInfo,
      proFeedbackPlays: data.playsInfo.filter((play) => play.type !== Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.ADD),
      proFeedbackCustomPlays: data.playsInfo.filter(
        (play) => play.type === Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.ADD
      ),
      purchase: data.purchase,
      submittedAt: data.submittedAt,
    };
    if (saveData.id > 0) {
      return this.httpClient.put(`${environment.apiHostname}/v1/pro-feedback/${saveData.id}`, saveData);
    } else {
      return this.httpClient.post(`${environment.apiHostname}/v1/pro-feedback`, saveData);
    }
  }
}
